import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    MdAdd,
    MdEdit,
    MdDelete,
    MdQrCode,
    MdDownload,
    MdVisibility,
    MdClose,
    MdSave,
    MdWarning,
    MdSettings,
    MdTableChart
} from 'react-icons/md';
import Notification from '../../components/Notification';
import { getActivities, getActivityTypes, createActivity, updateActivity, deleteActivity } from '../../api/activities';
import { exportRegistrationsExcel } from '../../api/registration';
import QRCode from 'qrcode';
import { formatWIBForInput, wibToUTC, formatActivityDateTime } from '../../utils/dateUtils';

const ActivityManagement = () => {
    const [activities, setActivities] = useState([]);
    const [activityTypes, setActivityTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [creating, setCreating] = useState(false);

    // Edit functionality
    const [showEditForm, setShowEditForm] = useState(false);
    const [editingActivity, setEditingActivity] = useState(null);
    const [updating, setUpdating] = useState(false);

    // Delete functionality
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingActivity, setDeletingActivity] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // QR Code functionality
    const [showQRCode, setShowQRCode] = useState(false);
    const [qrCodeData, setQRCodeData] = useState(null);
    const [generatingQR, setGeneratingQR] = useState(false);

    // Password protection
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const ADMIN_PASSWORD = 'admin123'; // Hardcoded password

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        activityTypeId: '',
        startAt: '',
        endAt: '',
        isActive: true,
        memberPrefix: ''
    });

    const navigate = useNavigate();

    useEffect(() => {
        if (isAuthenticated) {
            fetchData();
        }
    }, [isAuthenticated]);

    const handlePasswordSubmit = (e) => {
        e.preventDefault();
        if (password === ADMIN_PASSWORD) {
            setIsAuthenticated(true);
            setError(null);
        } else {
            setError('Invalid password');
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const [activitiesResponse, activityTypesResponse] = await Promise.all([
                getActivities(),
                getActivityTypes()
            ]);

            // Handle activities response - check for different response structures
            if (activitiesResponse) {
                if (activitiesResponse.success && activitiesResponse.data) {
                    setActivities(Array.isArray(activitiesResponse.data) ? activitiesResponse.data : []);
                } else if (Array.isArray(activitiesResponse)) {
                    setActivities(activitiesResponse);
                } else if (activitiesResponse.data) {
                    setActivities(Array.isArray(activitiesResponse.data) ? activitiesResponse.data : []);
                }
            }

            // Handle activity types response
            if (activityTypesResponse) {
                if (activityTypesResponse.success && activityTypesResponse.data) {
                    setActivityTypes(Array.isArray(activityTypesResponse.data) ? activityTypesResponse.data : []);
                } else if (Array.isArray(activityTypesResponse)) {
                    setActivityTypes(activityTypesResponse);
                } else if (activityTypesResponse.data) {
                    setActivityTypes(Array.isArray(activityTypesResponse.data) ? activityTypesResponse.data : []);
                }
            }
        } catch (err) {
            setError('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCreateActivity = async (e) => {
        e.preventDefault();
        setCreating(true);
        setError(null);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('activityTypeId', formData.activityTypeId);
            formDataToSend.append('startAt', wibToUTC(formData.startAt));
            formDataToSend.append('endAt', wibToUTC(formData.endAt));
            formDataToSend.append('isActive', formData.isActive);
            formDataToSend.append('memberPrefix', formData.memberPrefix);

            const response = await createActivity(formDataToSend);

            if (response) {
                setSuccess('Activity created successfully');
                setShowCreateForm(false);
                resetForm();
                fetchData();
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to create activity');
        } finally {
            setCreating(false);
        }
    };

    const handleEditClick = (activity) => {
        setEditingActivity(activity);
        setFormData({
            name: activity.name,
            description: activity.description,
            activityTypeId: activity.activityTypeId || activity.activityType?.id || '',
            startAt: formatWIBForInput(activity.startAt),
            endAt: formatWIBForInput(activity.endAt),
            isActive: activity.isActive !== undefined ? activity.isActive : true,
            memberPrefix: activity.memberPrefix || ''
        });
        setShowEditForm(true);
        setShowCreateForm(false);
    };

    const handleUpdateActivity = async (e) => {
        e.preventDefault();
        setUpdating(true);
        setError(null);

        try {
            const dataToSend = {
                name: formData.name,
                description: formData.description,
                activityTypeId: formData.activityTypeId,
                startAt: wibToUTC(formData.startAt),
                endAt: wibToUTC(formData.endAt),
                isActive: formData.isActive,
                memberPrefix: formData.memberPrefix
            };

            await updateActivity(editingActivity.id, dataToSend);
            setSuccess('Activity updated successfully');
            setShowEditForm(false);
            setEditingActivity(null);
            resetForm();
            fetchData();
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to update activity');
        } finally {
            setUpdating(false);
        }
    };

    const handleDeleteClick = (activity) => {
        setDeletingActivity(activity);
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        setDeleting(true);
        setError(null);

        try {
            await deleteActivity(deletingActivity.id);
            setSuccess('Activity deleted successfully');
            setShowDeleteConfirm(false);
            setDeletingActivity(null);
            fetchData();
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to delete activity');
        } finally {
            setDeleting(false);
        }
    };

    const handleGenerateQR = async (activity) => {
        setGeneratingQR(true);
        setError(null);

        try {
            const activityType = activity.activityType?.name?.toLowerCase();
            let urlPath = 'event';

            if (activityType === 'fan_page' || activityType === 'fan page') {
                urlPath = 'fan';
            }

            const baseUrl = window.location.origin;
            const registrationUrl = `${baseUrl}/${urlPath}/${activity.id}/register`;

            const qrCodeDataUrl = await QRCode.toDataURL(registrationUrl, {
                width: 300,
                margin: 2,
                color: {
                    dark: '#000000',
                    light: '#FFFFFF'
                }
            });

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            const qrSize = 300;
            const titleHeight = 80;
            const padding = 20;
            canvas.width = qrSize + (padding * 2);
            canvas.height = qrSize + titleHeight + (padding * 2);

            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            const qrImage = new Image();
            qrImage.onload = () => {
                const qrX = (canvas.width - qrSize) / 2;
                const qrY = padding;
                ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

                ctx.fillStyle = '#000000';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                const maxWidth = canvas.width - (padding * 2);
                const titleY = qrY + qrSize + (titleHeight / 2);
                const titleX = canvas.width / 2;

                let fontSize = 24;
                ctx.font = `bold ${fontSize}px Arial, sans-serif`;

                while (ctx.measureText(activity.name).width > maxWidth && fontSize > 12) {
                    fontSize -= 2;
                    ctx.font = `bold ${fontSize}px Arial, sans-serif`;
                }

                ctx.fillText(activity.name, titleX, titleY);

                const finalImageDataUrl = canvas.toDataURL('image/png');

                setQRCodeData({
                    activity: activity,
                    url: registrationUrl,
                    qrCode: finalImageDataUrl
                });
                setShowQRCode(true);
            };

            qrImage.src = qrCodeDataUrl;
        } catch (err) {
            setError('Failed to generate QR code');
        } finally {
            setGeneratingQR(false);
        }
    };

    const handleViewAdminPage = (activity) => {
        navigate(`/admin/${activity.id}`);
    };

    const handleExportExcel = async (activity) => {
        try {
            setError(null);
            const blob = await exportRegistrationsExcel(activity.id);

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `registrations-${activity.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.xlsx`;
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setSuccess(`Excel export for "${activity.name}" has been downloaded`);
        } catch (err) {
            console.error('Export error:', err);
            setError(err.response?.data?.error || err.message || 'Failed to export Excel file');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            activityTypeId: '',
            startAt: '',
            endAt: '',
            isActive: true,
            memberPrefix: ''
        });
    };

    const closeForm = () => {
        setShowEditForm(false);
        setShowCreateForm(false);
        setEditingActivity(null);
        resetForm();
    };

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <MdSettings className="w-8 h-8 text-blue-600" />
                        </div>
                        <h1 className="text-2xl font-bold mb-2 text-gray-900">Super Admin Access Required</h1>
                        <p className="text-gray-600">Enter password to continue</p>
                    </div>

                    {error && (
                        <Notification
                            type="error"
                            message={error}
                            onClose={() => setError(null)}
                        />
                    )}

                    <form onSubmit={handlePasswordSubmit} className="space-y-4">
                        <div>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter admin password"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required
                            />
                        </div>
                        <button
                            type="submit"
                            className="w-full p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
                        >
                            Access Admin Panel
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading activities...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto p-6">
                {error && (
                    <Notification
                        type="error"
                        message={error}
                        onClose={() => setError(null)}
                    />
                )}

                {success && (
                    <Notification
                        type="success"
                        message={success}
                        onClose={() => setSuccess(null)}
                    />
                )}

                {/* Header Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                        <div className="mb-6 lg:mb-0">
                            <div className="flex items-center space-x-4 mb-2">
                                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <MdSettings className="w-6 h-6 text-blue-600" />
                                </div>
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">Activity Management</h1>
                                    <p className="text-gray-600">Create and manage system activities</p>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={() => setShowCreateForm(!showCreateForm)}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
                        >
                            {showCreateForm ? <MdClose className="mr-2" /> : <MdAdd className="mr-2" />}
                            {showCreateForm ? 'Cancel' : 'New Activity'}
                        </button>
                    </div>
                </div>

                {/* Create Form */}
                {showCreateForm && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                        <div className="flex items-center space-x-3 mb-6">
                            <MdAdd className="text-blue-600 text-xl" />
                            <h2 className="text-xl font-semibold text-gray-900">Create New Activity</h2>
                        </div>

                        <form onSubmit={handleCreateActivity} className="space-y-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Activity Name
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Enter activity name"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Activity Type
                                    </label>
                                    <select
                                        name="activityTypeId"
                                        value={formData.activityTypeId}
                                        onChange={handleInputChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    >
                                        <option value="">Select Activity Type</option>
                                        {activityTypes.map((type) => (
                                            <option key={type.id} value={type.id}>
                                                {type.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows="3"
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                    placeholder="Enter activity description"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Start Date & Time
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="startAt"
                                        value={formData.startAt}
                                        onChange={handleInputChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        End Date & Time
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="endAt"
                                        value={formData.endAt}
                                        onChange={handleInputChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Activity Status
                                </label>
                                <div className="flex space-x-4">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="isActive"
                                            checked={formData.isActive === true}
                                            onChange={() => setFormData(prev => ({ ...prev, isActive: true }))}
                                            className="mr-2"
                                        />
                                        <span className="text-sm text-gray-700">Active</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="isActive"
                                            checked={formData.isActive === false}
                                            onChange={() => setFormData(prev => ({ ...prev, isActive: false }))}
                                            className="mr-2"
                                        />
                                        <span className="text-sm text-gray-700">Inactive</span>
                                    </label>
                                </div>
                            </div>

                            {formData.activityTypeId === 'FAN_PAGE' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Member Prefix
                                    </label>
                                    <input
                                        type="text"
                                        name="memberPrefix"
                                        value={formData.memberPrefix}
                                        onChange={handleInputChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Enter member prefix (e.g., TC24)"
                                        maxLength="10"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        This prefix will be used for fan page members (max 10 characters)
                                    </p>
                                </div>
                            )}

                            <div className="flex justify-end">
                                <button
                                    type="submit"
                                    disabled={creating}
                                    className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors duration-200"
                                >
                                    {creating ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <MdSave className="mr-2" />
                                            Create Activity
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Edit Form */}
                {showEditForm && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                        <div className="flex items-center space-x-3 mb-6">
                            <MdEdit className="text-blue-600 text-xl" />
                            <h2 className="text-xl font-semibold text-gray-900">Edit Activity</h2>
                        </div>

                        <form onSubmit={handleUpdateActivity} className="space-y-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Activity Name
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Enter activity name"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Activity Type
                                    </label>
                                    <select
                                        name="activityTypeId"
                                        value={formData.activityTypeId}
                                        onChange={handleInputChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    >
                                        <option value="">Select Activity Type</option>
                                        {activityTypes.map((type) => (
                                            <option key={type.id} value={type.id}>
                                                {type.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows="3"
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                    placeholder="Enter activity description"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Start Date & Time
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="startAt"
                                        value={formData.startAt}
                                        onChange={handleInputChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        End Date & Time
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="endAt"
                                        value={formData.endAt}
                                        onChange={handleInputChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Activity Status
                                </label>
                                <div className="flex space-x-4">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="isActive"
                                            checked={formData.isActive === true}
                                            onChange={() => setFormData(prev => ({ ...prev, isActive: true }))}
                                            className="mr-2"
                                        />
                                        <span className="text-sm text-gray-700">Active</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="isActive"
                                            checked={formData.isActive === false}
                                            onChange={() => setFormData(prev => ({ ...prev, isActive: false }))}
                                            className="mr-2"
                                        />
                                        <span className="text-sm text-gray-700">Inactive</span>
                                    </label>
                                </div>
                            </div>

                            {formData.activityTypeId === 'FAN_PAGE' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Member Prefix
                                    </label>
                                    <input
                                        type="text"
                                        name="memberPrefix"
                                        value={formData.memberPrefix}
                                        onChange={handleInputChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Enter member prefix (e.g., TC24)"
                                        maxLength="10"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        This prefix will be used for fan page members (max 10 characters)
                                    </p>
                                </div>
                            )}

                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={closeForm}
                                    className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors duration-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={updating}
                                    className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors duration-200"
                                >
                                    {updating ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <MdSave className="mr-2" />
                                            Update Activity
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Activities List */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <MdTableChart className="text-blue-600 text-xl" />
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">Activities Overview</h2>
                                    <p className="text-gray-600">{activities.length} total activities</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        {activities.length === 0 ? (
                            <div className="text-center py-16">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <MdTableChart className="w-8 h-8 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No Activities Found</h3>
                                <p className="text-gray-500">Create your first activity to get started</p>
                            </div>
                        ) : (
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="text-left py-4 px-6 text-sm font-medium text-gray-700">Activity</th>
                                        <th className="text-left py-4 px-4 text-sm font-medium text-gray-700">Type</th>
                                        <th className="text-left py-4 px-4 text-sm font-medium text-gray-700">Start Date</th>
                                        <th className="text-left py-4 px-4 text-sm font-medium text-gray-700">End Date</th>
                                        <th className="text-left py-4 px-4 text-sm font-medium text-gray-700">Status</th>
                                        <th className="text-left py-4 px-4 text-sm font-medium text-gray-700">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {activities.map((activity) => (
                                        <tr key={activity.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="py-4 px-6">
                                                <div>
                                                    <h3 className="font-medium text-gray-900 mb-1">{activity.name}</h3>
                                                    <p className="text-sm text-gray-500 truncate max-w-xs">{activity.description}</p>
                                                </div>
                                            </td>
                                            <td className="py-4 px-4">
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {activity.activityType?.name || 'Unknown'}
                                                </span>
                                            </td>
                                            <td className="py-4 px-4">
                                                <div className="text-sm text-gray-900">
                                                    {formatActivityDateTime(activity.startAt)}
                                                </div>
                                            </td>
                                            <td className="py-4 px-4">
                                                <div className="text-sm text-gray-900">
                                                    {formatActivityDateTime(activity.endAt)}
                                                </div>
                                            </td>
                                            <td className="py-4 px-4">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${activity.isActive
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    <span className={`w-1.5 h-1.5 rounded-full mr-1 ${activity.isActive ? 'bg-green-500' : 'bg-red-500'
                                                        }`}></span>
                                                    {activity.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="py-4 px-4">
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => handleViewAdminPage(activity)}
                                                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                        title="View Admin Page"
                                                    >
                                                        <MdVisibility className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleExportExcel(activity)}
                                                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                                        title="Export to Excel"
                                                    >
                                                        <MdDownload className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleGenerateQR(activity)}
                                                        disabled={generatingQR}
                                                        className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50"
                                                        title="Generate QR Code"
                                                    >
                                                        <MdQrCode className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEditClick(activity)}
                                                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                        title="Edit Activity"
                                                    >
                                                        <MdEdit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClick(activity)}
                                                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Delete Activity"
                                                    >
                                                        <MdDelete className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <MdWarning className="w-8 h-8 text-red-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Activity</h3>
                                <p className="text-gray-600 mb-6">
                                    Are you sure you want to delete "<strong>{deletingActivity?.name}</strong>"? This action cannot be undone.
                                </p>
                                <div className="flex space-x-3 justify-center">
                                    <button
                                        onClick={() => {
                                            setShowDeleteConfirm(false);
                                            setDeletingActivity(null);
                                        }}
                                        disabled={deleting}
                                        className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleDeleteConfirm}
                                        disabled={deleting}
                                        className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
                                    >
                                        {deleting ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                Deleting...
                                            </>
                                        ) : (
                                            <>
                                                <MdDelete className="mr-2" />
                                                Delete
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* QR Code Modal */}
                {showQRCode && qrCodeData && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <MdQrCode className="w-8 h-8 text-blue-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Registration QR Code</h3>
                                <p className="text-gray-600 mb-6">
                                    QR Code for "<strong>{qrCodeData.activity.name}</strong>"
                                </p>

                                {/* QR Code Image */}
                                <div className="mb-6 flex justify-center">
                                    <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                                        <img
                                            src={qrCodeData.qrCode}
                                            alt="Registration QR Code"
                                            className="w-64 h-64"
                                        />
                                    </div>
                                </div>

                                {/* Registration URL */}
                                <div className="mb-6">
                                    <p className="text-sm font-medium text-gray-700 mb-2">Registration URL:</p>
                                    <div className="bg-gray-100 p-3 rounded-lg text-sm text-gray-800 break-all">
                                        {qrCodeData.url}
                                    </div>
                                </div>

                                <div className="flex space-x-3 justify-center">
                                    <button
                                        onClick={() => {
                                            setShowQRCode(false);
                                            setQRCodeData(null);
                                        }}
                                        className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors duration-200"
                                    >
                                        Close
                                    </button>
                                    <button
                                        onClick={() => {
                                            const link = document.createElement('a');
                                            link.download = `qr-code-${qrCodeData.activity.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.png`;
                                            link.href = qrCodeData.qrCode;
                                            link.click();
                                        }}
                                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
                                    >
                                        <MdDownload className="mr-2" />
                                        Download QR Code
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ActivityManagement;